import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, first, tap } from 'rxjs/operators';
import { ContentType } from 'src/app/shared/enums/contentType';
import { getUserType } from 'src/app/shared/utils/SubscriptionUtil';
import { setExpirationTime } from '../../../utils/LoginUtils';
import { getApiUrl } from '../../../utils/apiUtils';
import { KEYS } from '../../../utils/constants/storage.constants';
import {
  getAuthHeaders,
  getBaseRequestData,
  getHeaders,
  isSuccessfulResponse,
} from '../../../utils/evergentUtils';
import { ConfigService } from '../../config.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { AccountProfileService } from '../account/account-profile.service';
import { GetEntitlementsRequestMessage as EntitlementRequestMessage } from '../payment/model/get-entitlements/get-entitlements.request.model';
import { AccountServiceMessage } from '../payment/model/get-entitlements/get-entitlements.response.model';
import { GetEntitlementsRequestMessage } from './model/entitlement/entitlement.request.message';
import { EntitlementRequestModel } from './model/entitlement/entitlement.request.model';
import { GetEntitlementsResponseMessage } from './model/entitlement/entitlement.response.message';
import { RecordPlayTimeRequestMessage } from './model/entitlement/record-play-time.request.message';
import { RecordPlayTimeRequestModel } from './model/entitlement/record-play-time.request.model';
import { RecordPlayTimeResponseMessage } from './model/entitlement/record-play-time.response.message';
import {
  GetVODCatalogRequestMessage,
  VODCatalogRequestMessageModel,
} from './model/entitlement/vod-catalog-request-message.model';
import { GetVODCatalogResponseMessage } from './model/entitlement/vod-catalog-response-message.model';
import { vodDetails } from './model/entitlement/vod-catalog-response.model';
import { UserEntitlementService } from './user-entitlement.service';
/**
 * Interface for TVOD price information
 */
export interface TvodPrice {
  mainTvodPrice: string;
}

@Injectable({
  providedIn: 'root',
})
export class EntitlementService {
  /** Flag indicating if TVOD purchase is allowed */
  private tvodPurchaseAllowed: boolean = false;

  /** Cached list of TVOD catalogs */
  private _tvodCatalogs: vodDetails[];

  entitlement$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private localStorage: LocalStorageService,
    private accountProfileService: AccountProfileService,
    private userEntitlementService: UserEntitlementService
  ) {}

  public get isTvodPurchaseAllowed(): boolean {
    return this.tvodPurchaseAllowed;
  }

  // should be used carefully below funciton has to be triggered
  get tvodCatalog(): vodDetails[] {
    return this._tvodCatalogs;
  }

  resetTvodCatalog(): void {
    this._tvodCatalogs = null;
    this.localStorage.removeItem(KEYS.TVOD_MOVIE_CATALOGS);
  }

  async tvodCatalogs(): Promise<vodDetails[]> {
    if (!this._tvodCatalogs) {
      const isLoggedIn =
        this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN)?.length > 0;
      const response = await this.getVODCatalog(isLoggedIn).toPromise();
      if (response) {
        if (isSuccessfulResponse(response?.GetVODCatalogResponseMessage)) {
          this.localStorage.setItem(
            'vodCatalogResponse',
            JSON.stringify(response?.GetVODCatalogResponseMessage?.vodDetails)
          );
          return this.handleVodCatalogResult(response);
        }
      }
    }
    return this._tvodCatalogs;
  }

  handleVodCatalogResult(vodCatalogResult: GetVODCatalogResponseMessage) {
    this.tvodPurchaseAllowed =
      vodCatalogResult?.GetVODCatalogResponseMessage?.purchaseAllowed || false;
    this.localStorage.setItem(
      KEYS.TVOD_PURCHASE_ALLOWED,
      JSON.stringify(this.tvodPurchaseAllowed)
    );
    this._tvodCatalogs =
      vodCatalogResult?.GetVODCatalogResponseMessage?.vodDetails?.filter(
        (tvodCatalog: vodDetails) =>
          tvodCatalog?.productCategory
            ?.toLowerCase()
            .includes(ContentType.MOVIE)
      );

    if (this._tvodCatalogs) {
      this.localStorage.setItem(
        KEYS.TVOD_MOVIE_CATALOGS,
        JSON.stringify(this._tvodCatalogs)
      );
    }
    return this._tvodCatalogs;
  }

  getEntitlement(
    contactId: string
  ): Observable<GetEntitlementsResponseMessage> {
    const url = getApiUrl(
      this.configService.applicationConfig.UmsURL,
      'GET_ENTITLEMENTS'
    );
    const requestMessage = new GetEntitlementsRequestMessage();
    const requestContent: EntitlementRequestModel = {
      ...new EntitlementRequestModel(),
      contactID: contactId,
      returnUpgradableFlag: true,
      // returnProductAttributes: true,
      ...getBaseRequestData(this.configService.applicationConfig),
    };

    requestMessage.GetEntitlementsRequestMessage = requestContent;
    return this.http
      .post<GetEntitlementsResponseMessage>(url, requestMessage, {
        headers: this.getHeaders(),
      })
      .pipe(
        first(),
        tap((item) => this.handleEntitlementResponse(item)),
        catchError(this.handleError)
      );
  }

  private handleEntitlementResponse(
    item: GetEntitlementsResponseMessage
  ): void {
    if (item.GetEntitlementsResponseMessage?.failureMessage?.length === 0) {
      try {
        const data =
          item?.GetEntitlementsResponseMessage?.AccountServiceMessage?.filter(
            (res: AccountServiceMessage) => !res?.isContent
          )?.[0];

        this.localStorage.setItem(
          KEYS.SUBSCRIPTION_PACKAGE,
          data?.serviceName || ''
        );
        this.localStorage.setItem(KEYS.STATUS, data?.status);
      } catch (e) {
        console.error('Error processing entitlement status:', e);
      }
    } else {
      this.localStorage.setItem(
        KEYS.SUBSCRIPTIONS,
        JSON.stringify(item.GetEntitlementsResponseMessage)
      );
    }

    const userStatus =
      item?.GetEntitlementsResponseMessage?.AccountServiceMessage?.filter(
        (res: AccountServiceMessage) => !res?.isContent
      )?.[0]?.status;
    this.localStorage.setItem(KEYS.STATUS, userStatus);
  }

  public getSubscriptionInfo(
    contactID: string,
    returnProductAttributes: boolean,
    returnUpgradableFlag: boolean
  ): Observable<GetEntitlementsResponseMessage> {
    const url = getApiUrl(
      this.configService.applicationConfig.UmsURL,
      'GET_ENTITLEMENTS'
    );
    const requestMessage = new EntitlementRequestMessage();

    requestMessage.GetEntitlementsRequestMessage = {
      ...getBaseRequestData(this.configService.applicationConfig),
      contactID,
      returnProductAttributes,
      returnUpgradableFlag,
    };

    return this.http.post<GetEntitlementsResponseMessage>(url, requestMessage, {
      headers: this.getHeaders(),
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Backend returned code ${error.status}, body was: ${error.error}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  getVODCatalog(isLoggedIn: boolean): Observable<GetVODCatalogResponseMessage> {
    const vodCatalogRequestMessage = this.getVODCatalogRequestMessage();
    const url = getApiUrl(
      this.configService.applicationConfig.UmsURL,
      'GET_VOD_CATALOG'
    );

    return this.http.post<GetVODCatalogResponseMessage>(
      url,
      vodCatalogRequestMessage,
      isLoggedIn ? getAuthHeaders(this.localStorage) : getHeaders()
    );
  }

  private getVODCatalogRequestMessage(): GetVODCatalogRequestMessage {
    const requestMessage = new GetVODCatalogRequestMessage();
    const requestContent: VODCatalogRequestMessageModel = {
      ...new VODCatalogRequestMessageModel(),
      returnPromotions: 'T',
      ...getBaseRequestData(this.configService.applicationConfig),
    };

    requestMessage.GetVODCatalogRequestMessage = requestContent;
    return requestMessage;
  }

  public getContentPriceDetails(resourceVm: ResourceVm): TvodPrice {
    this._tvodCatalogs = JSON.parse(
      this.localStorage.getItem(KEYS.TVOD_MOVIE_CATALOGS) || '[]'
    );
    return this.getTvodPriceDetails(resourceVm, this._tvodCatalogs);
  }

  private getTvodPriceDetails(
    resourceVm: ResourceVm,
    tvodCatalogs: vodDetails[]
  ): TvodPrice {
    const selectedTvodCatalog = tvodCatalogs?.find(
      (tvodCatalog: vodDetails) =>
        tvodCatalog?.skuORQuickCode ===
        resourceVm?.tvodPurchaseConfiguration?.sku?.[0]
    );

    return {
      mainTvodPrice: selectedTvodCatalog?.retailPrice?.toString() || '',
    };
  }

  getRecordPlayTime(
    assetId: string
  ): Observable<RecordPlayTimeResponseMessage> {
    const recordPlayTimeRequestMessage = this.recordPlayTimeRequest(assetId);
    const url = getApiUrl(
      this.configService.applicationConfig.UmsURL,
      'RECORD_PLAY_TIME'
    );

    return this.http.post<RecordPlayTimeResponseMessage>(
      url,
      recordPlayTimeRequestMessage,
      getAuthHeaders(this.localStorage)
    );
  }

  private recordPlayTimeRequest(assetId: string): RecordPlayTimeRequestMessage {
    const requestMessage = new RecordPlayTimeRequestMessage();
    const requestContent: RecordPlayTimeRequestModel = {
      ...new RecordPlayTimeRequestModel(),
      assetId,
      ...getBaseRequestData(this.configService.applicationConfig),
    };

    requestMessage.RecordPlayTimeRequestMessage = requestContent;
    return requestMessage;
  }

  private getHeaders(): HttpHeaders {
    const token = this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
    return new HttpHeaders({
      'content-type': 'application/json',
      Authorization: `Bearer ${
        token === 'undefined'
          ? this.configService.applicationConfig.umsApiToken
          : token
      }`,
    });
  }

  handleEntitlementResult(entitleResult: GetEntitlementsResponseMessage): void {
    if (entitleResult.GetEntitlementsResponseMessage?.ovatToken) {
      this.localStorage.setItem(
        KEYS.EVERGENT_OVAT_TOKEN,
        entitleResult.GetEntitlementsResponseMessage.ovatToken
      );
      setExpirationTime(
        entitleResult.GetEntitlementsResponseMessage.ovatTokenExpiry,
        KEYS.EVERGENT_OVAT_TOKEN_EXPIRY
      );
    } else {
      throw new Error('No entitlement found');
    }
  }

  async fetchEntitlements(contactId?: string, captureMetrics: boolean = false) {
    const selectedProfileId =
      contactId || this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID);
    const entitlementResult =
      await this.getEntitlement(selectedProfileId).toPromise();
    this.accountProfileService.isUpgrade =
      entitlementResult?.GetEntitlementsResponseMessage?.AccountServiceMessage?.filter(
        (res: AccountServiceMessage) => !res?.isContent
      )?.[0]?.name === this.configService.applicationConfig?.comboPackKey
        ? false
        : true;
    try {
      this.handleEntitlementResult(entitlementResult);
      this.userEntitlementService.entitlementRes =
        entitlementResult.GetEntitlementsResponseMessage;
      this.userEntitlementService.ahaUserType = getUserType(
        this.accountProfileService.account,
        entitlementResult
      );
      this.userEntitlementService.setSubscriptionAction();
      // capture login metrics
      if (captureMetrics) {
        /*   this.clevertapService.loginUser(
        contactID,
        `${currentProfileInfo[0]?.firstName} ${currentProfileInfo[0]?.lastName}`,
        mainUser?.email
      ); */
        /*     this.sendLoginSuccessToAnalytics(
        contactDetails,
        entitlementResult?.GetEntitlementsResponseMessage?.message
      ); */
        /* this.clevertapService.addEvent(ClevertapEvent.SUBSCRIPTION, {
        planData: this.dataService?.viewPlanData?.productInfo,
        ...this.subscriptionData(
          this.accountProfile,
          this.contactProfile,
          entitlementResult
        ),
      }); */
      }
    } catch (e) {
      return Promise.reject(e);
      // this.newrelicSessionService.errorEvent(e, ErrorEvents.SESSION_ERROR);
    }
    this.entitlement$.next(true);
    return Promise.resolve(entitlementResult.GetEntitlementsResponseMessage);
  }
}
